import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { useAuth } from '../../AuthContext';
import { detectExtension, connectExtension, sendToExtension, EXTENSION_STORE_URL } from '../../extension';

/* --------------------------------------------------------------------------
   Skool connect.

   Skool has no public API, so the SchedPilot Chrome extension sends the Skool
   session of this browser to SchedPilot (see schedpilot-chrome-extension/
   background.js). This button asks the extension to do that, after the user
   has accepted the risk notice once. Without the extension it links to the
   Chrome Web Store instead.
   -------------------------------------------------------------------------- */

const CONSENT_KEY = 'sp_skool_consent';
export const SKOOL_RISK_NOTICE =
  'SchedPilot uses your Skool browser session to post on your behalf. Skool has no official API; use at your own risk.';

const readConsent = () => { try { return localStorage.getItem(CONSENT_KEY) === '1'; } catch (e) { return false; } };

function SkoolConnect({ label = 'Connect Skool', compact = false }) {
  const { getAccountsData } = useAuth();
  const [consented, setConsented] = useState(readConsent);
  const [ticked, setTicked] = useState(false);
  const [busy, setBusy] = useState(false);
  const [missing, setMissing] = useState(false);
  const [needsSkoolLogin, setNeedsSkoolLogin] = useState(false);

  useEffect(() => { detectExtension().then((ext) => setMissing(!ext)); }, []);

  const connect = async () => {
    // Reconnect (compact) is only offered for accounts that already went through this.
    if (!consented && !compact) {
      if (!ticked) return toast.error('Tick the box to confirm you understand how Skool is connected.');
      try { localStorage.setItem(CONSENT_KEY, '1'); } catch (e) { /* private mode */ }
      setConsented(true);
    }

    setBusy(true);
    setNeedsSkoolLogin(false);
    try {
      const ext = await detectExtension();
      if (!ext) { setMissing(true); return; }
      setMissing(false);

      // Log the extension in with this app's session if it is not already.
      if (!ext.loggedIn) {
        const auth = await connectExtension();
        if (!auth.ok) return toast.error('Could not connect the extension to your SchedPilot account. Log in again and retry.');
      }

      const r = await sendToExtension({ type: 'CONNECT_SKOOL' }, 30000);
      if (r && r.ok) {
        toast.success(`Skool connected${r.account?.name ? ' as ' + r.account.name : ''}.`);
        getAccountsData();
      } else if (r && r.reason === 'NOT_LOGGED_IN_SKOOL') {
        setNeedsSkoolLogin(true);
      } else {
        toast.error((r && r.message) || 'Could not connect Skool. Open the SchedPilot extension and try from there.');
      }
    } finally {
      setBusy(false);
    }
  };

  if (missing) {
    return (
      <div style={{ fontSize: '0.75rem', color: '#64748b' }}>
        {!compact && <p style={{ margin: '0 0 8px' }}>Skool connects through the SchedPilot Chrome extension.</p>}
        <a href={EXTENSION_STORE_URL} target="_blank" rel="noopener noreferrer" className="btn btn-sm w-100"
           style={{ border: '1px solid #d1d9e0', borderRadius: 7, color: '#0f172a', fontWeight: 600 }}>
          Install the extension
        </a>
        <button type="button" onClick={() => detectExtension().then((ext) => setMissing(!ext))}
                style={{ border: 'none', background: 'none', color: '#64748b', fontSize: '0.72rem', marginTop: 6, cursor: 'pointer' }}>
          Installed it? Check again
        </button>
      </div>
    );
  }

  return (
    <div>
      {!consented && !compact && (
        <label style={{ display: 'flex', gap: 6, alignItems: 'flex-start', textAlign: 'left', fontSize: '0.72rem', color: '#64748b', marginBottom: 8, cursor: 'pointer' }}>
          <input type="checkbox" checked={ticked} onChange={(e) => setTicked(e.target.checked)} style={{ marginTop: 2 }} />
          <span>{SKOOL_RISK_NOTICE} <strong>I understand.</strong></span>
        </label>
      )}
      <button
        type="button"
        className={compact ? 'btn btn-sm' : 'btn btn-sm w-100'}
        onClick={connect}
        disabled={busy || (!consented && !compact && !ticked)}
        style={compact
          ? { border: '1px solid #cbd5e1', borderRadius: 7, color: '#0f172a', fontWeight: 600, background: '#fff', fontSize: '0.78rem' }
          : { background: '#0f172a', border: 'none', borderRadius: 7, color: '#fff', fontWeight: 600, padding: '8px 0' }}
      >
        {busy ? 'Connecting…' : label}
      </button>
      {needsSkoolLogin && (
        <div style={{ fontSize: '0.72rem', color: '#b45309', marginTop: 8 }}>
          Log in to skool.com in this browser first, then try again.{' '}
          <a href="https://www.skool.com/" target="_blank" rel="noopener noreferrer">Open skool.com</a>
        </div>
      )}
    </div>
  );
}

/* The extension, as a whole: lets people hand their app login to the extension
   with one click instead of typing their password into it. */
function ExtensionBanner() {
  const [ext, setExt] = useState(undefined);   // undefined = checking, null = not installed
  const [busy, setBusy] = useState(false);

  useEffect(() => { detectExtension().then(setExt); }, []);

  if (ext === undefined || ext === null) return null;   // nothing to say until it is installed

  const link = async () => {
    setBusy(true);
    const r = await connectExtension();
    setBusy(false);
    if (r.ok) {
      toast.success('Extension connected to your SchedPilot account.');
      setExt({ ...ext, loggedIn: true });
    } else {
      toast.error('Could not connect the extension. Log in again and retry.');
    }
  };

  return (
    <div className="d-flex align-items-center gap-3 mb-4" style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 12, padding: '10px 14px', fontSize: '0.85rem', color: '#334155' }}>
      <span style={{ flexGrow: 1 }}>
        {ext.loggedIn
          ? <>SchedPilot Chrome extension v{ext.version} is connected.</>
          : <>The SchedPilot Chrome extension is installed but not linked to your account.</>}
      </span>
      {!ext.loggedIn && (
        <button type="button" className="btn btn-sm" onClick={link} disabled={busy}
                style={{ background: '#0f172a', color: '#fff', borderRadius: 7, fontWeight: 600 }}>
          {busy ? 'Connecting…' : 'Connect extension'}
        </button>
      )}
    </div>
  );
}

export { SkoolConnect, ExtensionBanner };
